// Modules
import { Outlet } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toggleDarkMode, toggleSidebarMinimized } from 'features/ui/uiSlice';
import useNotifications from 'hooks/useNotifications';
import useVersionCheck from 'hooks/useVersionCheck';
import useAuthSync from 'hooks/useAuthSync';
import { useAppSelector } from 'app/store';
import { useWelcomeTour } from 'hooks/useWelcomeTour';
import { featureConfig } from 'config/features';

// UI
import { SidebarLayout } from 'components/catalyst/sidebar-layout';
import SidebarMain from './SidebarMain';
import SidebarNavbar from './SidebarNavbar';
import NotificationsDialog from 'components/notifications/NotificationsDialog';
import { WelcomeTourDialog } from 'components/welcomeTour';
import { BookOpen } from 'lucide-react';

const BOOKING_URL = 'https://calendly.com/nimax-builder/setup-call';

const MainLayout = () => {
  const { t } = useTranslation('welcomeTour');
  const { isDarkMode, isSidebarMinimized } = useAppSelector((state) => state.ui);
  const dispatch = useDispatch();

  // Check if app version changed - forces logout if updated
  useVersionCheck();

  // Sync user data from backend to keep fields like isEmailVerified up-to-date
  useAuthSync();

  const { showTour, openTour, closeTour, completeTour } = useWelcomeTour();
  const notificationsHandlers = useNotifications();

  const {
    showNotificationsModal,
    handleOpenNotificationsModal,
    unreadNotifications,
  } = notificationsHandlers;

  const unreadCount = unreadNotifications?.length ?? 0;

  const userMenuExtraItems = [
    ...(featureConfig.welcomeTour
      ? [{ label: t('SIDEBAR_WELCOME_TOUR'), icon: BookOpen, onClick: openTour }]
      : []),
  ];

  const handleToggleTheme = () => {
    dispatch(toggleDarkMode());
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebarMinimized());
  };

  return (
    <SidebarLayout
      isMinimized={isSidebarMinimized}
      navbar={
        <SidebarNavbar
          isDarkMode={isDarkMode}
          toggleDarkMode={handleToggleTheme}
          handleOpenNotificationsModal={handleOpenNotificationsModal}
          unreadCount={unreadCount}
          userMenuExtraItems={userMenuExtraItems}
        />
      }
      sidebar={
        <SidebarMain
          isDarkMode={isDarkMode}
          toggleDarkMode={handleToggleTheme}
          showNotificationsModal={showNotificationsModal}
          handleOpenNotificationsModal={handleOpenNotificationsModal}
          isMinimized={isSidebarMinimized}
          toggleMinimized={handleToggleSidebar}
          unreadCount={unreadCount}
          userMenuExtraItems={userMenuExtraItems}
        />
      }
    >
      <Outlet />

      <NotificationsDialog {...notificationsHandlers} />

      {featureConfig.welcomeTour && (
        <WelcomeTourDialog
          open={showTour}
          onClose={closeTour}
          onComplete={completeTour}
          bookingUrl={BOOKING_URL}
        />
      )}
    </SidebarLayout>
  );
};

export default MainLayout;
